import { ENDPOINTS } from "@/api/endpoints";
// import { jwtDecode } from "jwt-decode";
import ky from "ky";
import { getAccessTokenSilentlyRef } from "@/auth/getAccessTokenSilentlyRef";
import {
  authRecoveryRef,
  isUnrecoverableAuthError,
} from "@/auth/authRecoveryRef";
import { getCookie, setCookie } from "./cookie";
import { removeAuthCookies } from "./utils";
import { CustomAlert } from "@/components/ui/Alert";
import * as Sentry from "@sentry/react";

const BASE_API_URL = import.meta.env.VITE_PUBLIC_BASE_API_URL;

// SP4c: exported so client.test.ts can call the beforeRetry hook directly.
export const _beforeRetryForTesting = async ({ request }: { request: Request }) => {
  const ref = getAccessTokenSilentlyRef.current;
  if (!ref) return;
  try {
    const fresh = await ref();
    setCookie("accessToken", fresh);
    request.headers.set("Authorization", `Bearer ${fresh}`);
  } catch (err) {
    if (isUnrecoverableAuthError(err)) {
      // The refresh token is dead — retrying is pointless. Bounce through Auth0
      // rather than letting the request loop against a 401.
      void authRecoveryRef.current?.();
      return;
    }
    // Refresh failed — let afterResponse handle the 401
    console.warn("[client] silent refresh failed:", err);
  }
};

// JHipster's ExceptionTranslator returns title="Bad Request" and a verbose
// internal `detail`; the actionable info is the error key in `message`
// (e.g. "error.jobinsightsummaryexists"). Translate the keys we care about to
// human-readable copy. Falls back to a real (non-generic) title, then a
// generic message.
const BACKEND_ERROR_MESSAGES: Record<string, string> = {
  "error.jobinsightsummaryexists":
    "Insights for the selected dates already exist.",
  "error.nodataavailable":
    "No data available to generate insights for the selected dates.",
  "error.jobinprogress":
    "This job is currently in progress. Please try again once it finishes.",
  "error.invalidScheduleJobId": "Invalid job. Please refresh and try again.",
};

const resolveBackendErrorMessage = (body: unknown): string => {
  const fallback = "Something went wrong.";
  if (!body || typeof body !== "object") return fallback;
  const { message, title } = body as { message?: unknown; title?: unknown };
  if (typeof message === "string" && BACKEND_ERROR_MESSAGES[message]) {
    return BACKEND_ERROR_MESSAGES[message];
  }
  // Some endpoints set a meaningful title; ignore JHipster's generic "Bad Request".
  if (typeof title === "string" && title && title !== "Bad Request") {
    return title;
  }
  return fallback;
};

export const apiClient = ky.create({
  prefixUrl: BASE_API_URL,
  retry: {
    limit: 1,
    methods: ["get", "post", "put", "patch", "delete"],
    statusCodes: [401],
  },
  timeout: 300000,
  headers: {
    "ngrok-skip-browser-warning": "true",
    "Content-Type": "application/json",
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        let token = getCookie("accessToken");
        if (!token && getAccessTokenSilentlyRef.current) {
          try {
            token = await getAccessTokenSilentlyRef.current();
            setCookie("accessToken", token);
          } catch (err) {
            if (isUnrecoverableAuthError(err)) {
              void authRecoveryRef.current?.();
              return;
            }
            console.warn("[client] beforeRequest silent mint failed:", err);
          }
        }
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      },
    ],
    beforeRetry: [_beforeRetryForTesting],
    afterResponse: [
      async (request, _, response) => {
        if (response.status === 400) {
          const error = new Error("Invalid request");
          Sentry.captureException(error, {
            tags: {
              endpoint: request.url,
              status: response.status,
            },
          });
          // Surface a human-readable message. JHipster's `title` is a generic
          // "Bad Request" and `detail` is an internal dump, so translate the
          // error key (body.message) via resolveBackendErrorMessage. clone()
          // keeps the body stream intact for the caller.
          let description = "Something went wrong.";
          try {
            const body = await response.clone().json();
            description = resolveBackendErrorMessage(body);
          } catch {
            // non-JSON / empty body — keep the generic fallback
          }
          // Single toast with the real message. Sentry is already captured
          // above, so we don't also call logAndAlertError (which would fire a
          // second, duplicate toast).
          CustomAlert.error({
            description,
          });
          return response;
        }
        if (
          (response.status === 401 || response.status === 403) &&
          !request.url.includes(ENDPOINTS.LOGIN)
        ) {
          // SP4c: check for wrong-app error from Threads BE's AppIdValidator
          try {
            const bodyText = await response.clone().text();
            if (
              bodyText.includes("legacy_threads_tenant_id") ||
              bodyText.toLowerCase().includes("app_id mismatch") ||
              bodyText.toLowerCase().includes("wrong_app")
            ) {
              window.location.href = "/auth/wrong-app";
              return response;
            }
          } catch {
            // ignore body-read failures; fall through to existing redirect
          }
          const error = new Error("Authentication failed");
          Sentry.captureException(error, {
            tags: {
              endpoint: request.url,
              status: response.status,
            },
          });
          // Auth0CookieSync publishes the recovery callback; it clears the
          // poisoned SDK cache and re-runs loginWithRedirect. If it isn't
          // mounted yet, fall back to a hard redirect — matching the wrong-app
          // branch above. Note the route is /auth/signin; /auth/login does not
          // exist.
          if (authRecoveryRef.current) {
            void authRecoveryRef.current();
          } else {
            removeAuthCookies();
            window.location.href = "/auth/signin";
          }
        }
        return response;
      },
    ],
  },
});

// interface DecodedToken {
//   exp: number;
// }

// const isTokenExpired = (): boolean => {
//   const token = getCookie("accessToken");
//   if (!token) return true;

//   try {
//     const decoded = jwtDecode<DecodedToken>(token);
//     const currentTime = Math.round(Date.now() / 1000);
//     return decoded.exp < currentTime;
//   } catch (error) {
//     console.error("Error decoding token:", error);
//     return true;
//   }
// };

// Chat API Client for AI Chat (separate service on port 8000)
const CHAT_API_URL = import.meta.env.VITE_CHAT_API_URL || "http://localhost:8000";

export const chatApiClient = ky.create({
  prefixUrl: CHAT_API_URL,
  retry: 0,
  timeout: 300000, // 5 minutes for AI responses
  headers: {
    "Content-Type": "application/json",
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        const token = getCookie("accessToken");
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      },
    ],
  },
});
